import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserMainButton } from './components/BrowserMainButton';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GlobalToast } from './components/GlobalToast';
import { Spinner } from './components/Spinner';
import { SessionKickedPopup } from './components/SessionKickedPopup';

// App shell: only horizontal centering + width cap. Height is owned by each
// page so two layout modes can coexist:
//   - Chrome-heavy pages (Home, Mine, Game, Activity, Notifications) use
//     .app-shell-h + overflow-hidden with a scrollable <main>. The chrome
//     stays glued to the visible viewport edges on every mobile browser,
//     including iOS Safari (URL-bar resize) and TGMA (viewportStableHeight).
//   - Sub-pages use min-h-screen with normal window scroll for content.
// Keeping the outer shell height-agnostic lets both work without conflict.
export default function App({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full justify-center bg-app">
      <div className="relative w-full max-w-app bg-surface">
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>{children}</Suspense>
        </ErrorBoundary>
        <OfflineNotice />
        <GlobalToast />
        <BrowserMainButton />
        <SessionKickedPopup />
      </div>
    </div>
  );
}

function RouteFallback() {
  return <Spinner containerClassName="app-shell-h bg-surface" />;
}

function OfflineNotice() {
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      className="pointer-events-none absolute left-1/2 z-50 w-[calc(100%-1.5rem)] max-w-[24rem] -translate-x-1/2 rounded-xl bg-black/85 px-4 py-3 text-center text-xs font-medium text-white shadow-lg"
      style={{
        top: 'calc(max(var(--tg-safe-top, 0px), env(safe-area-inset-top, 0px)) + 0.75rem)',
      }}
      role="status"
      aria-live="polite"
    >
      You&apos;re offline. Cached screens may still open, but live account actions require a
      connection.
    </div>
  );
}
